import React, { FC, useState } from 'react';
import { Alert, Box, TextField } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { submitSupportTicketTemplate } from 'apollo/templates/zendesk';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { recentErrors } from 'apollo/recent-errors';
import { maskPersonalIdentityNumbers } from 'utils/mask-personal-identity-numbers';
import { buildCrashSummary, crashDetailsForSupport } from './crash-summary';

type CrashReportProps = { error: unknown };

const recentErrorLines = (): string[] =>
    recentErrors().map(
        (recent) => `- ${recent.at} ${recent.operation}: ${recent.message}${recent.code ? ` (${recent.code})` : ''}`,
    );

/**
 * DD-3780: on the red screen, one step to tell support what happened. The ticket gets the
 * user's own words (masked) and the crash details; nothing is sent without the click.
 */
const CrashReport: FC<CrashReportProps> = ({ error }: CrashReportProps) => {
    const { t: translate } = useTranslation(['common']);
    const currentUser = currentUserVar();
    const [whatHappened, setWhatHappened] = useState('');
    const [state, setState] = useState<'IDLE' | 'SENT' | 'FAILED'>('IDLE');
    const [submitSupportTicket, { loading }] = useMutation(submitSupportTicketTemplate);

    if (!currentUser?.email) {
        return null;
    }
    if (state === 'SENT') {
        return <Alert severity="success">{translate('common:CRASH_REPORT_SENT')}</Alert>;
    }

    const send = async () => {
        const errors = recentErrorLines();
        const message = [
            maskPersonalIdentityNumbers(whatHappened.trim()) || translate('common:CRASH_REPORT_NO_DESCRIPTION'),
            '',
            crashDetailsForSupport(buildCrashSummary(error)),
            ...(errors.length ? ['', '--- Recent errors ---', ...errors] : []),
        ].join('\n');
        try {
            const { data } = await submitSupportTicket({
                variables: {
                    name: `${currentUser.name?.firstName ?? ''} ${currentUser.name?.lastName ?? ''}`.trim(),
                    email: currentUser.email,
                    message,
                },
            });
            setState(data?.submitSupportTicket === 'OK' ? 'SENT' : 'FAILED');
        } catch {
            setState('FAILED');
        }
    };

    return (
        <Box mt={3} data-test="crash-report">
            <Box mb={1}>{translate('common:CRASH_REPORT_INTRO')}</Box>
            <TextField
                fullWidth
                multiline
                minRows={2}
                maxRows={6}
                value={whatHappened}
                onChange={(event) => setWhatHappened(event.target.value)}
                placeholder={translate('common:CRASH_REPORT_PLACEHOLDER')}
                inputProps={{ maxLength: 2000 }}
                sx={{ bgcolor: 'background.paper', mb: 1 }}
            />
            {state === 'FAILED' ? (
                <Alert severity="error" sx={{ mb: 1 }}>
                    {translate('common:CRASH_REPORT_FAILED')}
                </Alert>
            ) : undefined}
            <LoadingButton variant="text" loading={loading} onClick={send}>
                {translate('common:CRASH_REPORT_SEND')}
            </LoadingButton>
        </Box>
    );
};

export default CrashReport;
